import React, { useEffect, useState } from "react";

/**
 * Custom text highlighter that creates a properly positioned underline animation
 * without using rough-notation to avoid global SVG positioning issues.
 */
export function Highlighter({
  children,
  action = "underline",
  color = "#FF9800",
  animationDuration = 800,
}) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Start animation after component mounts
    const timer = setTimeout(() => {
      setIsAnimating(true);
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  if (action === "underline") {
    return (
      <span className="relative inline-block">
        {children}
        <svg
          className="absolute left-0 w-full h-3 pointer-events-none"
          style={{
            bottom: '-6px',
            overflow: 'visible',
          }}
          viewBox="0 0 100 6"
          preserveAspectRatio="none"
        >
          <path
            d="M2,4 Q25,2 50,3.5 Q75,5 98,3"
            stroke={color}
            strokeWidth="1.2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '100',
              strokeDashoffset: isAnimating ? '0' : '100',
              transition: `stroke-dashoffset ${animationDuration}ms ease-out ${isAnimating ? '0ms' : '0ms'}`,
            }}
          />
          {/* Add slight variation for more handwritten feel */}
          <path
            d="M1,4.5 Q30,1.5 55,4 Q80,6 99,3.5"
            stroke={color}
            strokeWidth="0.6"
            fill="none"
            opacity="0.6"
            strokeLinecap="round"
            style={{
              strokeDasharray: '100',
              strokeDashoffset: isAnimating ? '0' : '100',
              transition: `stroke-dashoffset ${animationDuration + 100}ms ease-out ${isAnimating ? '100ms' : '0ms'}`,
            }}
          />
        </svg>
      </span>
    );
  }

  if (action === "highlight") {
    return (
      <span className="relative inline-block">
        {children}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{
            top: '2px',
            left: '-2px',
            width: 'calc(100% + 4px)',
            height: 'calc(100% + 4px)',
          }}
          viewBox="0 0 100 20"
          preserveAspectRatio="none"
        >
          <path
            d="M1,15 Q20,12 40,14 Q60,16 80,13 Q90,12 99,14 L99,18 Q80,16 60,18 Q40,20 20,17 Q10,16 1,18 Z"
            fill={color}
            opacity={isAnimating ? "0.3" : "0"}
            style={{
              transition: `opacity ${animationDuration}ms ease-out`,
            }}
          />
          {/* Add texture overlay for more realistic look */}
          <path
            d="M5,13 Q25,10 45,12 Q65,14 85,11 Q95,10 98,12"
            stroke={color}
            strokeWidth="0.5"
            fill="none"
            opacity={isAnimating ? "0.4" : "0"}
            style={{
              transition: `opacity ${animationDuration}ms ease-out ${animationDuration / 2}ms`,
            }}
          />
        </svg>
      </span>
    );
  }

  return <span>{children}</span>;
}

export default Highlighter;
